import React, { useState } from 'react';
import { playerGoalService } from '../../services/playerGoalService';
import type { PlayerGoal } from '../../types/playerCard';
import { Target, Plus, Edit2, Trash2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'react-hot-toast';

interface GoalEditorProps {
  cardId: string;
  goals: PlayerGoal[];
  onUpdate: () => void;
}

export const GoalEditor: React.FC<GoalEditorProps> = ({
  cardId,
  goals,
  onUpdate,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    goal_text: '',
    category: 'skill' as 'skill' | 'physical' | 'mental' | 'team',
    target_date: '',
    notes: '',
  });

  const handleAddGoal = async () => {
    if (!formData.goal_text.trim()) {
      toast.error('Goal text is required');
      return;
    }

    try {
      await playerGoalService.createGoal(cardId, {
        goal_text: formData.goal_text,
        category: formData.category,
        target_date: formData.target_date || undefined,
        notes: formData.notes || undefined,
      });

      toast.success('Goal created successfully');
      setIsAdding(false);
      setFormData({ goal_text: '', category: 'skill', target_date: '', notes: '' });
      onUpdate();
    } catch (error) {
      console.error('Error creating goal:', error);
      toast.error('Failed to create goal');
    }
  };

  const handleUpdateStatus = async (goalId: string, status: PlayerGoal['status']) => {
    try {
      await playerGoalService.updateGoalStatus(goalId, status);
      toast.success('Goal status updated');
      onUpdate();
    } catch (error) {
      console.error('Error updating goal status:', error);
      toast.error('Failed to update goal status');
    }
  };

  const handleDelete = async (goalId: string) => {
    if (!confirm('Are you sure you want to delete this goal?')) return;

    try {
      await playerGoalService.deleteGoal(goalId);
      toast.success('Goal deleted');
      onUpdate();
    } catch (error) {
      console.error('Error deleting goal:', error);
      toast.error('Failed to delete goal');
    }
  };

  const getStatusIcon = (status: PlayerGoal['status']) => {
    switch (status) {
      case 'achieved':
        return <CheckCircle className="text-green-500" size={18} />;
      case 'in_progress':
        return <Clock className="text-blue-500" size={18} />;
      case 'cancelled':
        return <XCircle className="text-red-500" size={18} />;
      default:
        return <Clock className="text-gray-400" size={18} />;
    }
  };

  const getStatusColor = (status: PlayerGoal['status']) => {
    switch (status) {
      case 'achieved':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Target size={20} />
          Development Goals
        </h3>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="btn-primary flex items-center gap-2"
          >
            <Plus size={16} />
            Add Goal
          </button>
        )}
      </div>

      {/* Add Goal Form */}
      {isAdding && (
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <h4 className="font-semibold text-gray-900">New Goal</h4>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Goal Text *
            </label>
            <textarea
              value={formData.goal_text}
              onChange={(e) => setFormData({ ...formData, goal_text: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={3}
              placeholder="Describe the goal..."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="skill">Skill</option>
                <option value="physical">Physical</option>
                <option value="mental">Mental</option>
                <option value="team">Team</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Date (Optional)
              </label>
              <input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              rows={2}
              placeholder="Additional notes..."
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleAddGoal} className="btn-primary">
              Save Goal
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setFormData({ goal_text: '', category: 'skill', target_date: '', notes: '' });
              }}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Goals List */}
      {goals.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Target className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-600">No goals yet. Create one to get started!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {goals.map((goal) => (
            <div
              key={goal.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {getStatusIcon(goal.status)}
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(goal.status)}`}>
                      {goal.status.replace('_', ' ')}
                    </span>
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                      {goal.category}
                    </span>
                  </div>
                  <p className="text-gray-900 font-medium mb-2">{goal.goal_text}</p>
                  {goal.target_date && (
                    <p className="text-sm text-gray-600">
                      Target: {new Date(goal.target_date).toLocaleDateString()}
                    </p>
                  )}
                  {goal.notes && (
                    <p className="text-sm text-gray-600 mt-2">{goal.notes}</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={goal.status}
                    onChange={(e) => handleUpdateStatus(goal.id, e.target.value as PlayerGoal['status'])}
                    className="text-sm border border-gray-300 rounded px-2 py-1"
                  >
                    <option value="not_started">Not Started</option>
                    <option value="in_progress">In Progress</option>
                    <option value="achieved">Achieved</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <button
                    onClick={() => handleDelete(goal.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

